import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import type { AppUser, AppUserCreateInput, AppUserUpdateInput } from '@/types';
import { logEvent } from './audit.service';

const COL = 'users';

function fromFirestore(id: string, data: Record<string, unknown>): AppUser {
  return {
    ...data,
    id,
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : String(data.createdAt ?? ''),
    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : String(data.updatedAt ?? ''),
    lastLoginAt: data.lastLoginAt instanceof Timestamp ? data.lastLoginAt.toDate().toISOString() : data.lastLoginAt as string | undefined,
  } as AppUser;
}

export const userService = {
  async getAll(tenantId?: string, companyId?: string): Promise<AppUser[]> {
    const q = companyId && tenantId
      ? query(collection(db, COL), where('tenantId', '==', tenantId), where('companyId', '==', companyId), orderBy('displayName'))
      : tenantId
        ? query(collection(db, COL), where('tenantId', '==', tenantId), orderBy('displayName'))
        : query(collection(db, COL), orderBy('displayName'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => fromFirestore(d.id, d.data()));
  },

  async getById(userId: string): Promise<AppUser | null> {
    const snap = await getDoc(doc(db, COL, userId));
    if (!snap.exists()) return null;
    return fromFirestore(snap.id, snap.data());
  },

  /**
   * User creation is handled by a Cloud Function (createUser) to securely:
   * 1. Create the Firebase Auth account with the given password
   * 2. Set custom claims (role, tenantId, companyId)
   * 3. Write the user document to Firestore
   */
  async create(input: AppUserCreateInput, performedBy: string): Promise<{ uid: string }> {
    const fn = httpsCallable<AppUserCreateInput, { uid: string }>(functions, 'createUser');
    try {
      const result = await fn(input);
      await logEvent({
        entityType: 'user',
        entityId: result.data.uid,
        action: 'create',
        tenantId: input.tenantId,
        companyId: input.companyId,
        performedBy,
      });
      return result.data;
    } catch (err: any) {
      const code = err?.code?.replace('functions/', '') ?? err?.code ?? 'unknown';
      const message = err?.message ?? 'Erro ao criar usuário';
      const wrapped = new Error(message) as any;
      wrapped.code = code;
      throw wrapped;
    }
  },

  async update(userId: string, input: AppUserUpdateInput, performedBy: string): Promise<void> {
    await updateDoc(doc(db, COL, userId), {
      ...input,
      updatedAt: serverTimestamp(),
      updatedBy: performedBy,
    });
    // If role/tenant/company changed, sync custom claims via Cloud Function
    if (input.role || input.tenantId !== undefined || input.companyId !== undefined || input.allowedCompanyIds !== undefined) {
      const fn = httpsCallable(functions, 'updateUserClaims');
      await fn({
        userId,
        role: input.role,
        tenantId: input.tenantId,
        companyId: input.companyId,
        allowedCompanyIds: input.allowedCompanyIds
      });
    }
    await logEvent({ entityType: 'user', entityId: userId, action: 'update', tenantId: null, performedBy });
  },

  async setStatus(userId: string, status: 'active' | 'inactive', performedBy: string): Promise<void> {
    await updateDoc(doc(db, COL, userId), {
      status,
      updatedAt: serverTimestamp(),
      updatedBy: performedBy,
    });
    await logEvent({ entityType: 'user', entityId: userId, action: 'update', tenantId: null, performedBy, newValue: { status } });
  },
};
